import { DrawingState } from './DrawingState';
import { UIState } from './UIState';
import { InteractionState } from './InteractionState';
import { ViewState } from './ViewState';
import { DrawingModeState } from './DrawingModeState';
import { NetworkState } from './NetworkState';
import { SettingsState } from './SettingsState';

/**
 * Main application state manager that coordinates all state classes
 * This replaces the monolithic 'a' object with focused state classes
 */
export class AppState {
  public readonly drawing: DrawingState;
  public readonly ui: UIState;
  public readonly interaction: InteractionState;
  public readonly view: ViewState;
  public readonly drawingMode: DrawingModeState;
  public readonly network: NetworkState;
  public readonly settings: SettingsState;

  constructor() {
    // Initialize all state classes
    this.drawing = new DrawingState();
    this.ui = new UIState();
    this.interaction = new InteractionState();
    this.view = new ViewState();
    this.drawingMode = new DrawingModeState();
    this.network = new NetworkState();
    this.settings = new SettingsState();
  }

  /**
   * Get the complete application state
   */
  getState() {
    return {
      drawing: this.drawing.getState(),
      ui: this.ui.getState(),
      interaction: this.interaction.getState(),
      view: this.view.getState(),
      drawingMode: this.drawingMode.getState(),
      network: this.network.getState(),
      settings: this.settings.getState(),
    };
  }

  /**
   * Reset all state to initial values
   */
  reset() {
    this.interaction.reset();
    this.interaction.resetCpoints();
    this.ui.setMode('none');
    this.drawing.resetSelectedShapes();
  }

  /**
   * Get all RxJS subjects for reactive programming
   */
  getSubjects() {
    return {
      // Drawing subjects
      shapes$: this.drawing.shapes$,
      selectedShapes$: this.drawing.selectedShapes$,
      vertices$: this.drawing.vertices$,
      
      // UI subjects
      mode$: this.ui.mode$,
      realScale$: this.ui.realScale$,
      stamp$: this.ui.stamp$,
      showBlockSearch$: this.ui.showBlockSearch$,
      showAlertDialog$: this.ui.showAlertDialog$,
      showErrorDialog$: this.ui.showErrorDialog$,
      showInputBlockCollectionDialog$: this.ui.showInputBlockCollectionDialog$,
      addToBlockCollection$: this.ui.addToBlockCollection$,
      updateUI$: this.ui.updateUI$,
      commandsUI$: this.ui.commandsUI$,
      
      // Interaction subjects
      mouse$: this.interaction.mouse$,
      isMouseDown$: this.interaction.isMouseDown$,
      ctrl$: this.interaction.ctrl$,
      
      // View subjects
      zoom$: this.view.zoom$,
      pan$: this.view.pan$,
      
      // Drawing mode subjects
      drawingMode$: this.drawingMode.drawingMode$,
      
      // Network subjects
      graphs$: this.network.graphs$,
      updateCalculations$: this.network.updateCalculations$,
      
      // Settings subjects
      settingsChanged$: this.settings.settingsChanged$,
    };
  }

  /**
   * Emit events (for compatibility)
   */
  emit(event: string, data?: any) {
    // This is a compatibility method - in the new system, use RxJS subjects directly
    console.warn(`emit('${event}') is deprecated. Use RxJS subjects directly instead.`);
  }
}

// Global app state instance
const appState = new AppState();

// Legacy compatibility - create a proxy object that mimics the old 'a' object
// This allows gradual migration without breaking existing code
export const a = new Proxy({} as any, {
  get(target, prop) {
    // Map old properties to new state classes
    switch (prop) {
      // Drawing-related properties
      case 'shapes':
        return appState.drawing.getShapes();
      case 'activeShapes':
        return appState.drawing.getState().activeShapes;
      case 'line':
        return appState.drawing.getLine();
      case 'symline':
        return appState.drawing.getSymLine();
      case 'circle':
        return appState.drawing.getCircle();
      case 'rectangle':
        return appState.drawing.getRectangle();
      case 'free':
        return appState.drawing.getFree();
      case 'selectFrame':
        return appState.drawing.getSelectFrame();
      case 'vertices':
        return appState.drawing.getVertices();

      // UI-related properties
      case 'mode':
        return appState.ui.getMode();
      case 'realScale':
        return appState.ui.getRealScale();
      case 'blockUI':
        return appState.ui.isBlockUI();
      case 'isPrintAreaVisible':
        return appState.ui.isPrintAreaVisible();
      case 'isStampPdfVisible':
        return appState.ui.isStampPdfVisible();

      // Interaction-related properties
      case 'isMouseDown':
        return appState.interaction.isMouseDown();
      case 'mouse':
        return appState.interaction.getMouse();
      case 'magnet':
        return appState.interaction.getMagnet();
      case 'magnetPosition':
        return appState.interaction.getMagnetPosition();
      case 'anglePosition':
        return appState.interaction.getAnglePosition();
      case 'clickMoveStart':
        return appState.interaction.getClickMoveStart();
      case 'clickCopyStart':
        return appState.interaction.getClickCopyStart();
      case 'clickRotateStart':
        return appState.interaction.getClickRotateStart();
      case 'clickLineStart':
        return appState.interaction.getClickLineStart();
      case 'start':
        return appState.interaction.getStart();
      case 'end':
        return appState.interaction.getEnd();
      case 'cpoints':
        return appState.interaction.getCpoints();
      case 'cpointsClick':
        return appState.interaction.getCpointClick();
      case 'cpointsInitAngleDeg':
        return appState.interaction.getCpointInitAngleDeg();
      case 'ctrl':
        return appState.interaction.isCtrlPressed();
      case 'currentInputField':
        return appState.interaction.getCurrentInputField();
      case 'userInputValue':
        return appState.interaction.getUserInputValue();

      // View-related properties
      case 'zl':
        return appState.view.getZoomLevel();
      case 'zlc':
        return appState.view.getZoomCoefficient();
      case 'pan':
        return appState.view.isPanEnabled();
      case 'isPanning':
        return appState.view.isPanning();
      case 'mag1':
        return appState.view.isMag1Enabled();
      case 'mag2':
        return appState.view.isMag2Enabled();
      case 'angle_snap':
        return appState.view.isAngleSnapEnabled();

      // Drawing mode properties
      case 'drawingMode':
        return appState.drawingMode.getDrawingMode();

      // Network-related properties
      case 'preNetInfo':
        return appState.network.getPreNetInfo();
      case 'graphs':
        return appState.network.getGraphs();
      case 'sigmaInstance':
        return appState.network.getSigmaInstance();

      // Legacy RxJS subjects - return actual subjects
      case 'mode$':
        return appState.ui.mode$;
      case 'realScale$':
        return appState.ui.realScale$;
      case 'selected$':
        return appState.drawing.selectedShapes$;
      case 'stamp$':
        return appState.ui.stamp$;
      case 'showBlockSearch$':
        return appState.ui.showBlockSearch$;
      case 'showAlertDialog$':
        return appState.ui.showAlertDialog$;
      case 'showErrorDialog$':
        return appState.ui.showErrorDialog$;
      case 'showInputBlockCollectionDialog$':
        return appState.ui.showInputBlockCollectionDialog$;
      case 'addToBlockCollection$':
        return appState.ui.addToBlockCollection$;
      case 'updateCalculations$':
        return appState.network.updateCalculations$;
      case 'drawingMode$':
        return appState.drawingMode.drawingMode$;
      case 'updateUI$':
        return appState.ui.updateUI$;
      case 'graph$':
        return appState.network.graphs$;

      default:
        console.warn(`Property "${String(prop)}" not found in new state structure`);
        return undefined;
    }
  },

  set(target, prop, value) {
    // Map old properties to new state classes
    switch (prop) {
      // Drawing-related properties
      case 'shapes':
        appState.drawing.setShapes(value);
        break;
      case 'vertices':
        appState.drawing.setVertices(value);
        break;

      // UI-related properties
      case 'mode':
        appState.ui.setMode(value);
        break;
      case 'realScale':
        appState.ui.setRealScale(value);
        break;
      case 'blockUI':
        appState.ui.setBlockUI(value);
        break;
      case 'isPrintAreaVisible':
        appState.ui.setPrintAreaVisible(value);
        break;
      case 'isStampPdfVisible':
        appState.ui.setStampPdfVisible(value);
        break;

      // Interaction-related properties
      case 'isMouseDown':
        appState.interaction.setMouseDown(value);
        break;
      case 'mouse':
        appState.interaction.setMouse(value);
        break;
      case 'magnet':
        appState.interaction.setMagnet(value);
        break;
      case 'magnetPosition':
        appState.interaction.setMagnetPosition(value);
        break;
      case 'anglePosition':
        appState.interaction.setAnglePosition(value);
        break;
      case 'clickMoveStart':
        appState.interaction.setClickMoveStart(value);
        break;
      case 'clickCopyStart':
        appState.interaction.setClickCopyStart(value);
        break;
      case 'clickRotateStart':
        appState.interaction.setClickRotateStart(value);
        break;
      case 'clickLineStart':
        appState.interaction.setClickLineStart(value);
        break;
      case 'start':
        appState.interaction.setStart(value);
        break;
      case 'end':
        appState.interaction.setEnd(value);
        break;
      case 'ctrl':
        appState.interaction.setCtrl(value);
        break;
      case 'currentInputField':
        appState.interaction.setCurrentInputField(value);
        break;
      case 'userInputValue':
        appState.interaction.setUserInputValue(value);
        break;

      // View-related properties
      case 'zl':
        appState.view.setZoomLevel(value);
        break;
      case 'zlc':
        appState.view.setZoomCoefficient(value);
        break;
      case 'pan':
        appState.view.setPanEnabled(value);
        break;
      case 'isPanning':
        appState.view.setPanning(value);
        break;
      case 'mag1':
        appState.view.setMag1Enabled(value);
        break;
      case 'mag2':
        appState.view.setMag2Enabled(value);
        break;
      case 'angle_snap':
        appState.view.setAngleSnapEnabled(value);
        break;

      // Drawing mode properties
      case 'drawingMode':
        appState.drawingMode.setDrawingMode(value);
        break;

      // Network-related properties
      case 'preNetInfo':
        appState.network.setPreNetInfo(value);
        break;
      case 'graphs':
        appState.network.setGraphs(value);
        break;
      case 'sigmaInstance':
        appState.network.setSigmaInstance(value);
        break;

      default:
        console.warn(`Setting property "${String(prop)}" not implemented in new state structure`);
        break;
    }
    return true;
  }
});

// Export the new app state for new code
export { appState };
